/**
 * iter1035 — BHI Priority Action Card (feature #1).
 * iter1039 — Unified Daily Card: merges Priority Action + Weekly Plan + 🔥 Streak.
 *
 * Single AI-selected top action for today. Renders above the two-column
 * dashboard. Reads from `/api/bhi/priority-action` and logs completion
 * via `POST /api/bhi/priority-action/complete`.
 *
 * iter1039 also fetches `/api/bhi/btre/weekly-plan` to display:
 *  - 🔥 streak badge (top-right)
 *  - Top 3 weekly-habit tasks with today's toggle
 *  - Link to full weekly plan
 *
 * Also respects the Reading Level toggle (feature #3): shows `plain_bg`
 * when the user has activated the "Explain-like-I'm-5" mode.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useReadingLevel } from "@/contexts/BhiReadingLevelContext";
import {
  Sparkles, Check, Loader2, Leaf, Sun, Moon, Brain, Activity,
  Droplet, Dumbbell, Cherry, Wheat, Info, RotateCw, Utensils, ChevronRight,
  Flame, CalendarDays, Target, Trophy, GitBranch,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  leaf:     Leaf,
  sun:      Sun,
  moon:     Moon,
  brain:    Brain,
  activity: Activity,
  droplet:  Droplet,
  dumbbell: Dumbbell,
  cherry:   Cherry,
  wheat:    Wheat,
};

// Category → visual tint
const CATEGORY_TINT = {
  nutrition:  { icon: "text-emerald-500",  ring: "from-emerald-500/25 to-lime-500/10",  chip: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  activity:   { icon: "text-orange-500",   ring: "from-orange-500/25 to-amber-500/10",  chip: "bg-orange-500/15 text-orange-600 dark:text-orange-400" },
  mindbody:   { icon: "text-indigo-500",   ring: "from-indigo-500/25 to-violet-500/10", chip: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" },
  sleep:      { icon: "text-slate-500",    ring: "from-slate-500/25 to-blue-500/10",    chip: "bg-slate-500/15 text-slate-600 dark:text-slate-400" },
  supplement: { icon: "text-purple-500",   ring: "from-purple-500/25 to-fuchsia-500/10",chip: "bg-purple-500/15 text-purple-600 dark:text-purple-400" },
};

export default function BhiPriorityActionCard({ isDark = false }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [completing, setCompleting] = useState(false);
  const [weekly, setWeekly] = useState(null);  // iter1039 — weekly plan + streak
  const [togglingTid, setTogglingTid] = useState(null);
  const { isPlain } = useReadingLevel();

  const load = useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const { data } = await axios.get(`${API}/bhi/priority-action`);
      setState({ loading: false, data, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: err?.response?.data?.detail || "Грешка" });
    }
  }, []);

  // iter1039 — fetch weekly plan for streak + top habit tasks
  const loadWeekly = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bhi/btre/weekly-plan`, { params: { top_n: 5 } });
      // iter1040 — Milestone celebrations. Fire a toast when the streak crosses
      // one of the milestone thresholds (3, 7, 14, 30, 60, 100 days).
      const newStreak = data?.stats?.streak || 0;
      try {
        const seen = JSON.parse(localStorage.getItem("bhi_streak_milestones_seen") || "[]");
        const milestones = [3, 7, 14, 30, 60, 100];
        for (const m of milestones) {
          if (newStreak >= m && !seen.includes(m)) {
            const emoji = m >= 100 ? "🏆" : m >= 30 ? "🎖️" : m >= 14 ? "🥇" : m >= 7 ? "🎉" : "🔥";
            toast.success(`${emoji} ${m} дни streak! Ти си на невероятен път.`, {
              duration: 5000,
              description: m >= 30
                ? "Достигна професионално ниво на постоянство. Продължавай!"
                : m >= 7
                  ? "Първата важна граница — навикът вече се вкоренява."
                  : "Стабилен старт — още 4 дни до първата седмица!",
            });
            seen.push(m);
            localStorage.setItem("bhi_streak_milestones_seen", JSON.stringify(seen));
            break;  // only one toast per fetch
          }
        }
      } catch { /* localStorage may be unavailable */ }
      setWeekly(data);
    } catch {
      // silent — priority action card still works without weekly plan
    }
  }, []);

  useEffect(() => { load(); loadWeekly(); }, [load, loadWeekly]);

  const todayIso = weekly?.today;
  const isDone = (tid) => (weekly?.checkins || []).some(
    (c) => c.task_id === tid && c.date === todayIso && c.done
  );

  const toggleWeeklyTask = async (task_id) => {
    if (!todayIso) return;
    setTogglingTid(task_id);
    const next = !isDone(task_id);
    // Optimistic update
    setWeekly((w) => {
      if (!w) return w;
      const others = (w.checkins || []).filter(
        (c) => !(c.task_id === task_id && c.date === todayIso)
      );
      return { ...w, checkins: [...others, { task_id, date: todayIso, done: next }] };
    });
    try {
      await axios.post(`${API}/bhi/btre/weekly-plan/toggle`, {
        task_id, date: todayIso, done: next,
      });
      // Refresh weekly to update streak accurately
      loadWeekly();
    } catch {
      toast.error("Не успях да запиша отметката.");
      loadWeekly();
    } finally {
      setTogglingTid(null);
    }
  };

  const handleComplete = async () => {
    const act = state.data?.action;
    if (!act) return;
    setCompleting(true);
    try {
      await axios.post(`${API}/bhi/priority-action/complete`, {
        recommendation_id: act.recommendation_id,
        pathway_anchor:    act.pathway_anchor,
      });
      toast.success(isPlain ? "Записано! Браво! 🎉" : "Отбелязано — записано в дневника ти.");
      await load();
      await loadWeekly();  // iter1039 — refresh streak
    } catch (err) {
      toast.error("Не успях да запиша. Опитай пак.");
    } finally {
      setCompleting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────
  if (state.loading) {
    return (
      <div className={`rounded-2xl border p-4 mb-6 flex items-center gap-3 ${
        isDark ? "bg-slate-900/50 border-slate-800" : "bg-white border-slate-200"
      }`} data-testid="bhi-priority-action-loading">
        <Loader2 className="w-4 h-4 animate-spin opacity-60" />
        <span className="text-sm opacity-70">Избирам ти най-подходящото действие за днес…</span>
      </div>
    );
  }

  // ── No action available ────────────────────────────────────
  if (!state.data?.action) {
    return (
      <div className={`rounded-2xl border p-4 mb-6 ${
        isDark ? "bg-emerald-900/20 border-emerald-800/50" : "bg-emerald-50 border-emerald-200"
      }`} data-testid="bhi-priority-action-done">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center shrink-0">
            <Check className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
              {isPlain ? "Браво! Направила си всичко важно днес 🎉" : "Всички приоритети за днес са изпълнени"}
            </div>
            <div className="text-xs opacity-70 mt-0.5">
              {state.data?.reasoning_bg || "Готова си за деня."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main action card ───────────────────────────────────────
  const act = state.data.action;
  const tint = CATEGORY_TINT[act.category] || CATEGORY_TINT.nutrition;
  const Icon = ICON_MAP[act.icon] || Leaf;
  const title = isPlain && act.plain_bg ? act.plain_bg : act.title_bg;
  const impactChip = act.impact_chip_bg;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-4 sm:p-5 mb-6 ${
        isDark ? "bg-gradient-to-br from-slate-900 to-slate-900/60 border-slate-800"
               : "bg-gradient-to-br from-white to-slate-50 border-slate-200"
      }`}
      data-testid="bhi-priority-action-card"
    >
      {/* Corner glow */}
      <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl bg-gradient-to-br ${tint.ring} pointer-events-none`} />

      {/* iter1039 — Streak badge (top-right). Renders only when we have streak data. */}
      {weekly?.stats && (
        <StreakBadge
          streak={weekly.stats.streak || 0}
          doneToday={(weekly.stats.tasks_completed_today || 0)}
          totalTasks={weekly.stats.tasks_total || 0}
          isDark={isDark}
        />
      )}
      <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-current/10 ${tint.icon}`}>
          <Icon className="w-6 h-6" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-pink-500 shrink-0" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-pink-500">
              {isPlain ? "Твоят фокус за днес" : "Приоритетно действие · днес"}
            </span>
          </div>
          <h3 className="text-base sm:text-lg font-bold leading-snug break-words" data-testid="bhi-priority-action-title">
            {title}
          </h3>
          {impactChip && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${tint.chip}`}>
                {impactChip}
              </span>
              <span className={`inline-flex items-start gap-1 text-[10px] break-words ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                <Info className="w-3 h-3 shrink-0 mt-0.5" />
                <span>{state.data?.reasoning_bg || ""}</span>
              </span>
            </div>
          )}

          {/* iter1040 — Personal impact estimate (answers "why does this matter for ME?") */}
          {state.data?.personal_impact_bg && (
            <div
              data-testid="bhi-priority-action-personal-impact"
              className={`mt-2 flex items-start gap-1.5 text-[11px] leading-snug rounded-md px-2 py-1.5 ${
                isDark ? "bg-pink-500/10 text-pink-200 border border-pink-500/20"
                       : "bg-pink-50 text-pink-800 border border-pink-100"
              }`}
            >
              <Target className="w-3 h-3 shrink-0 mt-0.5 opacity-80" />
              <span className="break-words">{state.data.personal_impact_bg}</span>
            </div>
          )}

          {/* iter1044 — Breadcrumb: risk factors → pathway → priority action */}
          {state.data?.activated_by?.drivers?.length > 0 && (
            <ActivatedByBreadcrumb activatedBy={state.data.activated_by} isDark={isDark} isPlain={isPlain} />
          )}

          {/* iter1038 — BHI ↔ Plate Simulator bridge: for nutrition actions, offer 1-click jump. */}
          {act.category === "nutrition" && (
            <Link
              to="/dashboard/diary"
              data-testid="bhi-priority-action-open-plate"
              className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-md px-2 py-1 transition
                ${isDark
                  ? "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                  : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
            >
              <Utensils className="w-3 h-3" />
              Отвори Plate Simulator
              <ChevronRight className="w-3 h-3 opacity-70" />
            </Link>
          )}
        </div>

        {/* CTAs — full-width on mobile, inline on sm+ */}
        <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0 sm:self-center">
          <button
            type="button"
            onClick={load}
            aria-label="Друга препоръка"
            title="Друга препоръка"
            data-testid="bhi-priority-action-refresh"
            className={`w-10 h-10 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0 ${
              isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-300" : "bg-slate-100 hover:bg-slate-200 text-slate-600"
            }`}
          >
            <RotateCw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleComplete}
            disabled={completing}
            data-testid="bhi-priority-action-complete"
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2.5 sm:py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-sm font-semibold transition"
          >
            {completing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Check className="w-4 h-4" />}
            {isPlain ? "Направих го" : "Отбележи като направено"}
          </button>
        </div>
      </div>

      {/* iter1039 — Weekly habits mini-strip (top 3 tasks with today's toggle) */}
      {weekly?.tasks?.length > 0 && (
        <WeeklyHabitsStrip
          weekly={weekly}
          isDone={isDone}
          onToggle={toggleWeeklyTask}
          togglingTid={togglingTid}
          isPlain={isPlain}
          isDark={isDark}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// iter1039 — Sub-components
// ─────────────────────────────────────────────────────────────

// iter1044 — Pathway breadcrumb (rf → pathway → priority)
const SOURCE_LABEL_BG = {
  auto_from_bhi:       "BHI рисков фактор",
  auto_from_dkg:       "DKG наследствен риск",
  auto_from_lab:       "Лабораторни данни",
  auto_from_genetics:  "Генетичен тест",
  user_confirmed:      "Потвърдено от теб",
};

function ActivatedByBreadcrumb({ activatedBy, isDark, isPlain }) {
  const [open, setOpen] = React.useState(false);
  const drivers = activatedBy.drivers || [];
  const extra = (activatedBy.driver_count || 0) - drivers.length;

  return (
    <div
      data-testid="bhi-priority-action-breadcrumb"
      className={`mt-2 rounded-md text-[11px] ${
        isDark ? "bg-purple-500/10 border border-purple-500/20 text-purple-200"
               : "bg-purple-50 border border-purple-200 text-purple-900"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="bhi-priority-action-breadcrumb-toggle"
        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left"
      >
        <GitBranch className="w-3 h-3 shrink-0 opacity-80" />
        <span className="flex-1 min-w-0 break-words">
          {isPlain
            ? "Защо това е важно точно за теб?"
            : (activatedBy.name_bg
                ? `Активен pathway: ${activatedBy.name_bg}`
                : "Виж защо този pathway е активен")}
        </span>
        <ChevronRight className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1.5">
          {drivers.map((d, i) => (
            <div
              key={i}
              className="flex items-start gap-1.5"
              data-testid={`bhi-priority-action-driver-${d.condition_id}`}
            >
              <span className={`inline-block w-1 h-1 mt-1.5 rounded-full shrink-0 ${
                d.source === "user_confirmed" ? "bg-emerald-500"
                : d.source === "auto_from_lab" ? "bg-pink-500"
                : d.source === "auto_from_dkg" ? "bg-blue-500"
                : d.source === "auto_from_genetics" ? "bg-fuchsia-500"
                : "bg-purple-500"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[11px] break-words">{d.label_bg}</div>
                <div className="opacity-60 text-[10px]">
                  {SOURCE_LABEL_BG[d.source] || d.source}
                </div>
              </div>
            </div>
          ))}
          {extra > 0 && (
            <div className="opacity-60 text-[10px] pl-2.5">
              + още {extra} свързани фактора
            </div>
          )}
          <div className="pt-1 mt-1 border-t border-purple-200/40 dark:border-purple-500/20 opacity-80 text-[10px] flex items-start gap-1">
            <Info className="w-2.5 h-2.5 shrink-0 mt-0.5" />
            <span>
              {isPlain
                ? "Всеки от тези фактори тласка този pathway. Днешното действие го модулира."
                : `Weight ${activatedBy.weight || 1} · Priority ${activatedBy.priority || 1}. Действието модулира тази биологична каскада.`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function StreakBadge({ streak, doneToday, totalTasks, isDark }) {
  const active = streak > 0;
  // iter1040 — Milestone tier gives a trophy icon + special color for 30/60/100.
  const tier = streak >= 100 ? { icon: Trophy, label: "GOLD",   grad: "from-yellow-500 to-orange-500" }
             : streak >= 30  ? { icon: Trophy, label: "SILVER", grad: "from-slate-300 to-slate-500" }
             : streak >= 14  ? { icon: Trophy, label: "BRONZE", grad: "from-amber-500 to-orange-600" }
             : null;
  const IconComp = tier ? tier.icon : Flame;
  return (
    <div
      className={`absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold shadow-sm ${
        tier
          ? `bg-gradient-to-br ${tier.grad} text-white`
          : (active
              ? "bg-gradient-to-br from-orange-500 to-red-500 text-white"
              : (isDark ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-500"))
      }`}
      title={active
        ? `Streak: ${streak} последователни дни с ≥1 отметка${tier ? ` · ${tier.label} tier` : ""}`
        : "Направи първата отметка, за да започне streak-ът"}
      data-testid="bhi-daily-streak-badge"
    >
      <IconComp className={`w-3.5 h-3.5 ${active ? "" : "opacity-60"}`} />
      <span>{streak}</span>
      {totalTasks > 0 && (
        <span className="opacity-80 font-semibold">
          · {doneToday}/{totalTasks} днес
        </span>
      )}
    </div>
  );
}

function WeeklyHabitsStrip({ weekly, isDone, onToggle, togglingTid, isPlain, isDark }) {
  const topTasks = (weekly.tasks || []).slice(0, 3);
  if (!topTasks.length) return null;

  return (
    <div
      className={`mt-4 pt-3 border-t ${isDark ? "border-slate-800" : "border-slate-200"}`}
      data-testid="bhi-daily-weekly-strip"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <CalendarDays className={`w-3.5 h-3.5 ${isDark ? "text-sky-300" : "text-sky-600"}`} />
          <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-sky-300" : "text-sky-600"}`}>
            {isPlain ? "Моите навици тази седмица" : "Топ навици · тази седмица"}
          </span>
        </div>
        <Link
          to="/health-intelligence/breast/btre"
          data-testid="bhi-daily-weekly-full-plan"
          className={`text-[10px] font-semibold inline-flex items-center gap-0.5 transition ${
            isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          Пълен план
          <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <ul className="space-y-1.5">
        {topTasks.map((t) => {
          const done = isDone(t.task_id);
          const isToggling = togglingTid === t.task_id;
          return (
            <li key={t.task_id}>
              <button
                type="button"
                onClick={() => onToggle(t.task_id)}
                disabled={isToggling}
                data-testid={`bhi-daily-habit-${t.task_id}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition ${
                  done
                    ? (isDark ? "bg-emerald-500/10 hover:bg-emerald-500/20" : "bg-emerald-50 hover:bg-emerald-100")
                    : (isDark ? "hover:bg-slate-800/60" : "hover:bg-slate-100")
                }`}
              >
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 rounded-full border-2 shrink-0 transition ${
                    done
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : (isDark ? "border-slate-600" : "border-slate-300")
                  }`}
                >
                  {isToggling
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : (done ? <Check className="w-3 h-3" /> : null)}
                </span>
                <span className={`text-xs flex-1 min-w-0 break-words ${done ? "line-through opacity-70" : ""}`}>
                  {t.title_bg || t.title || t.factor_name || "Задача"}
                </span>
                {t.emoji && <span className="text-sm shrink-0" aria-hidden>{t.emoji}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
